import { BumpOffersProps, BumpsProps, BumpType } from 'contexts/OneClickBuyContext/types/Bumps';
import { ProductTypes } from 'contexts/OneClickBuyContext/types/Product';

export type OrderItemPayload = {
	offer_code: string;
	type: 'main' | 'bump' | 'combo';
	quantity: number;
};

export type KitOfferPricingKind = 'access' | 'freeAccess' | 'trial' | 'nextCharge' | 'price';

const asBumps = (bumps?: BumpsProps[] | null): BumpsProps[] => (Array.isArray(bumps) ? bumps : []);

const offerCodeOf = (offer?: { code?: string } | null): string | undefined => {
	if (!offer?.code) return undefined;
	return String(offer.code);
};

const uniqueCodes = (codes: Array<string | undefined>): string[] => {
	const seen = new Set<string>();
	const result: string[] = [];
	for (const code of codes) {
		if (!code || seen.has(code)) continue;
		seen.add(code);
		result.push(code);
	}
	return result;
};

export const isSubscription = (type?: string, typeOfCharge?: string): boolean =>
	type === ProductTypes.SUBSCRIPTION || type === ProductTypes.SOFTWARE || typeOfCharge === 'recurring';

export const isComboBump = (bump?: BumpsProps | null): boolean => bump?.type === BumpType.COMBO;

export const splitBumpsByType = (
	bumps?: BumpsProps[] | null
): { comboBumps: BumpsProps[]; regularBumps: BumpsProps[] } => {
	const comboBumps: BumpsProps[] = [];
	const regularBumps: BumpsProps[] = [];

	for (const bump of asBumps(bumps)) {
		if (isComboBump(bump)) comboBumps.push(bump);
		else regularBumps.push(bump);
	}

	return { comboBumps, regularBumps };
};

export const comboOfferCodes = (bumps?: BumpsProps[] | null): string[] => {
	const { comboBumps } = splitBumpsByType(bumps);
	return uniqueCodes(comboBumps.flatMap(bump => (bump.offers ?? []).map(offerCodeOf)));
};

export const buildBumpsCodes = (bumps?: BumpsProps[] | null, items: string[] = []): string[] => {
	const { comboBumps, regularBumps } = splitBumpsByType(bumps);
	const selected = Array.isArray(items) ? items : [];

	const comboCodes = comboOfferCodes(comboBumps);
	const selectedBumpCodes = uniqueCodes(
		regularBumps.flatMap(bump =>
			(bump.offers ?? []).map(offerCodeOf).filter((code): code is string => !!code && selected.includes(code))
		)
	);

	return uniqueCodes([...comboCodes, ...selectedBumpCodes]);
};

export const orderItemTypeFor = (bumps?: BumpsProps[] | null, offerCode?: string): BumpType => {
	if (!offerCode) return BumpType.BUMP;

	const matching = asBumps(bumps).filter(bump =>
		(bump.offers ?? []).some(offer => offerCodeOf(offer) === offerCode)
	);

	if (matching.some(isComboBump)) return BumpType.COMBO;
	return BumpType.BUMP;
};

const findOfferByCode = (bumps?: BumpsProps[] | null, code?: string): BumpOffersProps | undefined => {
	if (!code) return undefined;
	for (const bump of asBumps(bumps)) {
		const offer = (bump.offers ?? []).find(item => offerCodeOf(item) === code);
		if (offer) return offer;
	}
	return undefined;
};

export const sumAdditionalAmount = (bumps?: BumpsProps[] | null, items: string[] = []): number => {
	return buildBumpsCodes(bumps, items).reduce((total, code) => {
		const offer = findOfferByCode(bumps, code);
		if (!offer || Number(offer.trial_days) > 0) return total;
		return total + Number(offer.price || 0);
	}, 0);
};

export const buildOrderItems = (
	bumps?: BumpsProps[] | null,
	items: string[] = [],
	mainOfferCode?: string
): OrderItemPayload[] => {
	const selected = uniqueCodes([...comboOfferCodes(bumps), ...(Array.isArray(items) ? items : [])]);
	const seen = new Set<string>();
	const additional: OrderItemPayload[] = [];

	for (const bump of asBumps(bumps)) {
		for (const offer of bump.offers ?? []) {
			const code = offerCodeOf(offer);
			if (!code || !selected.includes(code) || seen.has(code)) continue;
			seen.add(code);
			additional.push({
				offer_code: code,
				type: orderItemTypeFor(bumps, code) === BumpType.COMBO ? 'combo' : 'bump',
				quantity: 1,
			});
		}
	}

	if (!mainOfferCode) return additional;

	return [
		...additional,
		{
			offer_code: mainOfferCode,
			type: 'main',
			quantity: 1,
		},
	];
};

export const bumpsCodesQuerySuffix = (codes?: string[] | null): string => {
	if (!Array.isArray(codes) || codes.length === 0) return '';
	return `?bumpsCodes=${codes.join(',')}`;
};

export const kitOfferPricingKind = (
	offer?: Pick<BumpOffersProps, 'trial_days' | 'next_charge_price' | 'price' | 'type_of_charge' | 'product'> | null,
	itensFree?: boolean
): KitOfferPricingKind => {
	if (!offer) return 'price';

	if (!isSubscription(offer.product?.type, offer.type_of_charge)) {
		return itensFree ? 'freeAccess' : 'access';
	}

	if (Number(offer.trial_days) > 0) return 'trial';
	if (itensFree) return 'freeAccess';
	if (Number(offer.next_charge_price) > 0 && offer.next_charge_price !== offer.price) return 'nextCharge';
	return 'freeAccess';
};
